import { useState, useEffect, useRef, useCallback } from "react";
import { carta } from "@/mocks/carta";

type SectionKey = "inicio" | "carta" | "contacto";

const NAV_LINKS: { label: string; id: SectionKey }[] = [
  { label: "Inicio", id: "inicio" },
  { label: "Nuestra Carta", id: "carta" },
  { label: "Contacto", id: "contacto" },
];

const CATEGORY_KEYS = Object.keys(carta) as (keyof typeof carta)[];

function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) {
    const offset = 72;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }
}

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>(CATEGORY_KEYS[0]);
  const [activeSection, setActiveSection] = useState<SectionKey>("inicio");

  const sectionRefs = useRef<Record<string, HTMLElement | null>>();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 60);

      const offsets = NAV_LINKS.map(({ id }) => {
        const el = document.getElementById(id);
        if (!el) return { id, top: Infinity };
        const rect = el.getBoundingClientRect();
        return { id, top: rect.top - 100 };
      });

      const current = offsets
        .filter((o) => o.top <= 0)
        .sort((a, b) => b.top - a.top)[0];

      if (current) {
        setActiveSection(current.id as SectionKey);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = useCallback((id: string) => {
    setMobileMenuOpen(false);
    scrollToId(id);
  }, []);

  const categoryData = carta[activeCategory as keyof typeof carta];

  return (
    <div className="min-h-screen bg-background-50 font-sans">
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-background-50/95 backdrop-blur-sm shadow-md py-2"
            : "bg-transparent py-4"
        }`}
      >
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <a
            href="#inicio"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick("inicio");
            }}
            className="font-heading font-bold text-lg md:text-xl tracking-tight"
            style={{
              color: scrolled
                ? "oklch(var(--foreground-950))"
                : "oklch(var(--background-50))",
            }}
          >
            La Fuente
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={`#${link.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.id);
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  activeSection === link.id
                    ? "bg-primary-500 text-background-50"
                    : scrolled
                      ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                      : "text-background-50/90 hover:text-background-50 hover:bg-white/10"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              scrolled
                ? "text-foreground-700 hover:bg-background-100"
                : "text-background-50 hover:bg-white/10"
            }`}
            aria-label="Menú"
          >
            <i
              className={`ri-${mobileMenuOpen ? "close" : "menu"}-line text-2xl`}
            />
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50/98 backdrop-blur-sm border-t border-background-200 shadow-lg">
            <div className="flex flex-col px-4 py-3 gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.id);
                  }}
                  className={`px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                    activeSection === link.id
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="inicio" className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Rustic%20Spanish%20roasted%20chicken%20with%20golden%20crispy%20skin%20on%20wooden%20table%2C%20fresh%20bread%20basket%2C%20warm%20golden%20lighting%2C%20traditional%20kitchen%20atmosphere%2C%20earthy%20tones%2C%20appetizing%20food%20photography%2C%20soft%20bokeh%20background%2C%20professional%20culinary%20photography&width=1600&height=900&seq=hero-lafuente-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>
        <div className="relative z-10 flex flex-col items-center justify-center py-24 md:py-40 px-4">
          <div className="animate-fade-in">
            <img
              src="https://static.readdy.ai/image/608e49fe07c67df2616301ff7cf83b22/5ce5442763f8a5bb0f902d3bdab5f735.jpeg"
              alt="Asador de Pollos y Bocatas La Fuente - Sabor Tradicional"
              className="w-full max-w-md md:max-w-xl lg:max-w-2xl rounded-lg shadow-2xl"
            />
          </div>
          <p className="mt-6 text-xl md:text-2xl text-background-50 font-heading tracking-wide animate-fade-in-delay">
            Sabor Tradicional
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 animate-fade-in-delay">
            <a
              href="#carta"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick("carta");
              }}
              className="px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Ver Nuestra Carta
            </a>
            <a
              href="tel:600012430"
              className="px-8 py-3 bg-background-50/20 text-background-50 font-semibold rounded-full hover:bg-background-50/30 transition-colors border border-background-50/30 whitespace-nowrap backdrop-blur-sm"
            >
              <i className="ri-phone-fill mr-2" />
              Llamar para pedir
            </a>
          </div>
        </div>
      </section>

      {/* Carta / Menu Section */}
      <section id="carta" className="w-full bg-background-50 py-14 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-full">
                <i className="ri-restaurant-fill text-lg text-primary-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                Nuestra Carta
              </h2>
            </div>
            <p className="text-foreground-600 text-base md:text-lg max-w-xl mx-auto">
              Disfruta de nuestros pollos asados, bocatas tradicionales y mucho más
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8">
            {CATEGORY_KEYS.map((key) => {
              const cat = carta[key];
              const isActive = activeCategory === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm md:text-base font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? "bg-primary-500 text-background-50 shadow-sm"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800"
                  }`}
                >
                  <i className={`${cat.icon} ${isActive ? "text-background-50" : "text-foreground-500"}`} />
                  {cat.title}
                </button>
              );
            })}
          </div>

          {/* Menu Items Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categoryData.items.map((item, index) => (
              <div
                key={`${activeCategory}-${index}`}
                className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-5 hover:border-primary-300/60 hover:bg-primary-50/40 transition-all duration-300 group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-foreground-900 text-base md:text-lg group-hover:text-primary-700 transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-foreground-500 text-sm mt-1 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <span className="text-primary-600 font-bold text-base md:text-lg whitespace-nowrap mt-0.5">
                    {item.price}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phone CTA Section */}
      <section id="pedidos" className="w-full bg-secondary-500 py-12 md:py-16 px-4">
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-background-50/20 rounded-full mb-5">
            <i className="ri-phone-fill text-3xl md:text-4xl text-background-50" />
          </div>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">
            Llámanos para pedir
          </h2>
          <a
            href="tel:600012430"
            className="mt-2 text-4xl md:text-6xl font-bold text-background-50 tracking-wider hover:scale-105 transition-transform duration-300 whitespace-nowrap"
          >
            600 012 430
          </a>
          <p className="mt-4 text-base md:text-lg text-background-50/80">
            Solo llamadas · Comida para llevar
          </p>
        </div>
      </section>

      {/* Contact Section: Location + Hours */}
      <section id="contacto" className="w-full bg-background-50 py-12 md:py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                <i className="ri-map-pin-2-fill text-lg text-secondary-700" />
              </div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                Contacto y Ubicación
              </h2>
            </div>
            <p className="text-foreground-600 text-base md:text-lg">
              Te esperamos en Algeciras. Consulta nuestro horario y pasa a recoger tu pedido.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
            {/* Address Info */}
            <div className="flex-1 w-full">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-map-pin-fill text-lg text-secondary-700" />
                </div>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  Dónde estamos
                </h3>
              </div>
              <div className="space-y-2 text-foreground-700 text-lg md:text-xl">
                <p className="font-medium text-foreground-900">Agrupación San Francisco</p>
                <p>Fuente Nueva, Algeciras</p>
              </div>
              <a
                href="https://maps.google.com/?q=Agrupación+San+Francisco,+Fuente+Nueva,+Algeciras"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-semibold text-base md:text-lg transition-colors"
              >
                <span>Ver en Google Maps</span>
                <i className="ri-external-link-line" />
              </a>

              <div className="mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-full">
                    <i className="ri-time-fill text-lg text-secondary-700" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                    Horario de apertura
                  </h3>
                </div>
                <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden shadow-sm">
                  <div className="grid grid-cols-3 bg-secondary-100 px-4 py-3">
                    <span className="text-sm font-semibold text-secondary-900">Día</span>
                    <span className="text-sm font-semibold text-secondary-900 text-center">Mañanas</span>
                    <span className="text-sm font-semibold text-secondary-900 text-center">Tardes</span>
                  </div>
                  {[
                    { day: "Lunes", morning: "12:00 – 15:30", evening: "19:30 – 00:00" },
                    { day: "Martes", morning: "12:00 – 15:30", evening: "Cerrado" },
                    { day: "Miércoles", morning: "Cerrado", evening: "Cerrado", closed: true },
                    { day: "Jueves", morning: "12:00 – 15:30", evening: "19:30 – 00:00" },
                    { day: "Viernes", morning: "12:00 – 15:30", evening: "19:30 – 00:00" },
                    { day: "Sábado", morning: "12:00 – 15:30", evening: "19:30 – 00:00" },
                    { day: "Domingo", morning: "12:00 – 15:30", evening: "19:30 – 00:00" },
                  ].map((item, index) => (
                    <div
                      key={item.day}
                      className={`grid grid-cols-3 px-4 py-3.5 items-center ${
                        index % 2 === 0 ? "bg-background-50" : "bg-background-100/50"
                      } ${item.closed ? "text-foreground-400" : "text-foreground-700"}`}
                    >
                      <span className={`font-medium ${item.closed ? "line-through" : ""}`}>
                        {item.day}
                      </span>
                      <span className="text-center text-sm md:text-base">{item.morning}</span>
                      <span className="text-center text-sm md:text-base">{item.evening}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2 text-secondary-700 text-sm">
                  <i className="ri-information-line" />
                  <span>Miércoles cerrado por descanso</span>
                </div>
              </div>
            </div>

            {/* Mini Map */}
            <div className="flex-1 w-full lg:sticky lg:top-24">
              <div className="rounded-xl overflow-hidden border-2 border-background-200 shadow-lg">
                <iframe
                  src="https://maps.google.com/maps?q=Agrupación+San+Francisco,+Fuente+Nueva,+Algeciras,+España&t=&z=16&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="360"
                  className="border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación de Asador de Pollos y Bocatas La Fuente"
                />
              </div>
              <div className="mt-3 flex items-start gap-2 text-foreground-500 text-sm">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 bg-accent-100 rounded-full">
                  <i className="ri-map-pin-fill text-accent-700 text-sm" />
                </div>
                <span>Asador de Pollos y Bocatas La Fuente, Agrupación San Francisco</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full bg-primary-700 py-6 px-4 text-center">
        <p className="text-sm md:text-base text-background-50/90">
          © 2026 Asador de Pollos y Bocatas La Fuente · Sabor Tradicional Creado por ©M.Murillo
        </p>
      </footer>
    </div>
  );
}